import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/common/layout/Header";
import { GradientButton } from "../../components/common/ui/Button";
import { getSession, createSession } from "@/utils/session";
import { getCartData, calculateTotal, clearCartData } from "@/utils/cart";
import { countries, findCountryDialCode } from "@/utils/countries";
import {
    getCustomerDomains, getCustomerInvoices, getCustomerDetails,
    getCustomerOrders, updateDomainNameServers,
    toggleDomainLock, renewDomain, updateCustomerDetails
} from "@/services/dreamscapeApi";
import {
    CheckCircle2, Bell, LogOut, Settings, Plus, LayoutDashboard, Globe, CreditCard, Clock, Lock, User,
    ChevronRight, Search, Wifi, AlertCircle, ShoppingCart, Loader2, Mail, Eye, X, Unlock, RefreshCw,
    ShieldCheck, Package, Trash2, Shield, Edit3, CreditCard as CardIcon
} from "lucide-react";
import { isValidABN, isValidACN } from "@/utils/validation";
import { API_URL } from "@/config/api";

type TabId = 'overview' | 'domains' | 'billing' | 'cart' | 'account';

interface DomainModalState {
    open: boolean;
    domain: any;
    tab: 'ns' | 'lock' | 'renew' | 'info' | 'edit-profile' | 'settings';
}

export default function Profile() {
    const navigate = useNavigate();
    const [session] = useState(getSession());
    const [activeTab, setActiveTab] = useState<TabId>('overview');
    const [loading, setLoading] = useState(true);
    const [customer, setCustomer] = useState<any>(null);
    const [domains, setDomains] = useState<any[]>([]);
    const [invoices, setInvoices] = useState<any[]>([]);
    const [orders, setOrders] = useState<any[]>([]);
    const [cartData, setCartData] = useState(getCartData());
    const [error, setError] = useState<string | null>(null);
    const [domainSearch, setDomainSearch] = useState('');
    const [actionLoading, setActionLoading] = useState<'ns' | 'lock' | 'renew' | 'update' | null>(null);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const [timeRemaining, setTimeRemaining] = useState<number>(0);

    const [modal, setModal] = useState<DomainModalState>({ open: false, domain: null, tab: 'info' });
    const [nameServers, setNameServers] = useState<{ host: string }[]>([]);
    const [renewPeriod, setRenewPeriod] = useState(1);
    const [selectedCountry, setSelectedCountry] = useState('AU');

    useEffect(() => {
        if (!session?.customerId) {
            navigate('/login');
            return;
        }
        loadData();
    }, [session, navigate]);

    useEffect(() => {
        if (toast) {
            const timer = setTimeout(() => setToast(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [toast]);

    useEffect(() => {
        if (modal.open && modal.tab === 'edit-profile' && customer) {
            setSelectedCountry(customer.country || customer.country_code || 'AU');
        }
    }, [modal.open, modal.tab, customer]);

    useEffect(() => {
        if (session?.expiresAt) {
            const updateTimer = () => {
                const diff = Math.max(0, Math.floor((session.expiresAt - Date.now()) / 60000));
                setTimeRemaining(diff);
            };
            updateTimer();
            const timer = setInterval(updateTimer, 60000);
            return () => clearInterval(timer);
        }
    }, [session]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [custRes, domRes, invRes, ordRes] = await Promise.all([
                getCustomerDetails(Number(session.customerId)),
                getCustomerDomains(Number(session.customerId)),
                getCustomerInvoices(Number(session.customerId)),
                getCustomerOrders(session.email || '')
            ]);

            if (custRes.success && custRes.customer) {
                // Detailed unwrapping to catch every possible Dreamscape response variation
                let data = custRes.customer;
                if (data && data.customer) data = data.customer;
                if (data && data.status === true && data.data) data = data.data;
                if (data && data.status === 'success' && data.data) data = data.data;
                if (data && data.details) data = { ...data, ...data.details };
                if (data && data.registrant) data = { ...data, ...data.registrant };

                // Final safety check for registry-level errors
                if (data && (data.status === false || data.status === 'error')) {
                    console.warn("Registry returned inner error object:", data);
                    setCustomer(null);
                } else if (data) {
                    setCustomer(data);

                    // SYNC: Keep the header initials in sync with live registry/profile data 
                    const first = data.firstName || data.first_name || '';
                    const last = data.lastName || data.last_name || '';
                    if (session && (first !== session.firstName || last !== session.lastName)) {
                        createSession(
                            session.customerId,
                            session.email,
                            session.username,
                            first || session.firstName,
                            last || session.lastName,
                            180
                        );
                    }
                }
            }
            if (domRes.success) setDomains(domRes.domains || []);
            // Combine real invoices from Dreamscape and local test orders
            let finalInvoices = invRes.success ? (invRes.invoices || []) : [];

            if (ordRes.success) {
                setOrders(ordRes.orders || []);

                // Extract test/pending orders that shouldn't normally be in Dreamscape invoices yet
                const localTestInvoices = (ordRes.orders || [])
                    .filter((o: any) => o.status === 'completed_test' || o.status === 'pending')
                    .map((o: any) => ({
                        id: o.dreamscape_order_id || o.id,
                        issueDate: o.created_at || new Date().toISOString(),
                        amount: o.amount || o.total_price || '0.00', // Use amount first if available
                        status: o.status === 'completed_test' ? 'test' : o.status,
                        isDummy: o.status === 'completed_test',
                        domainName: o.domain_name || o.domainName || ''
                    }));

                // deduplicate if needed (though usually test orders won't be in Dreamscape yet)
                const existingIds = new Set(finalInvoices.map((inv: any) => inv.id.toString()));
                const uniqueLocals = localTestInvoices.filter(loc => !existingIds.has(loc.id.toString()));

                finalInvoices = [...finalInvoices, ...uniqueLocals];
            }

            setInvoices(finalInvoices);
            if (domRes.success) setDomains(domRes.domains || []);
        } catch (err: any) {
            setError(err.message || "Failed to load profile data");
        } finally {
            setLoading(false);
        }
    };

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => setToast({ msg, type });


    const handleUpdateProfile = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!session?.customerId) return;

        const fd = new FormData(e.currentTarget);
        const data = {
            firstName: fd.get('firstName'),
            lastName: fd.get('lastName'),
            phone: fd.get('phone'),
            address: fd.get('address'),
            city: fd.get('city'),
            state: fd.get('state'),
            postCode: fd.get('postCode'),
            country: fd.get('country'),
            businessName: fd.get('businessName'),
            businessNumberType: fd.get('businessNumberType') || 'ABN',
            businessNumber: fd.get('businessNumber'),
        };

        // Validation for business account
        if (customer?.accountType === 'business' || data.businessName) {
            const bNum = (data.businessNumber as string) || '';
            const bType = (data.businessNumberType as string) || 'ABN';

            if (bType === 'ABN' && bNum && !isValidABN(bNum)) {
                showToast("Invalid ABN checksum. Please provide a valid 11-digit ABN.", "error");
                return;
            }
            if (bType === 'ACN' && bNum && !isValidACN(bNum)) {
                showToast("Invalid ACN checksum. Please provide a valid 9-digit ACN.", "error");
                return;
            }
        }

        try {
            setActionLoading('update');
            const res = await updateCustomerDetails(session.customerId, data);
            if (res.success) {
                // Update local storage and session for UI consistency (Avatar, etc)
                const newFirstName = (data.firstName as string) || '';
                const newLastName = (data.lastName as string) || '';

                if (session) {
                    createSession(
                        session.customerId,
                        session.email,
                        session.username,
                        newFirstName || session.firstName,
                        newLastName || session.lastName,
                        180
                    );
                }

                showToast("Profile updated successfully", "success");
                setModal(m => ({ ...m, open: false }));
                loadData();
            } else {
                showToast(res.message || "Failed to update profile", "error");
            }
        } catch (err: any) {
            showToast(err.message || "Update failed", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleUpdateSettings = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        showToast("Settings saved successfully", "success");
        setModal(m => ({ ...m, open: false }));
    };

    const handleDownloadInvoice = (invoiceId: number) => {
        window.open(`${API_URL}/domain/invoice/${invoiceId}/download`, '_blank');
    };

    const handleSaveNs = async () => {
        if (!modal.domain) return;
        try {
            setActionLoading('ns');
            const res = await updateDomainNameServers(modal.domain.id, nameServers.filter(ns => ns.host.trim()));
            if (res.success) {
                showToast("Nameservers updated successfully");
                setModal(m => ({ ...m, open: false }));
            } else {
                showToast(res.message || "Update failed", "error");
            }
        } catch (err) {
            showToast("An error occurred", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleToggleLock = async (dom: any) => {
        try {
            setActionLoading('lock');
            const res = await toggleDomainLock(dom.id, !dom.isLocked);
            if (res.success) {
                showToast(`Domain ${dom.isLocked ? 'unlocked' : 'locked'} successfully`);
                setDomains(prev => prev.map(d => d.id === dom.id ? { ...d, isLocked: !dom.isLocked } : d));
            } else {
                showToast(res.message || "Update failed", "error");
            }
        } catch (err) {
            showToast("An error occurred", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleRenew = async () => {
        if (!modal.domain) return;
        try {
            setActionLoading('renew');
            const res = await renewDomain(modal.domain.id, renewPeriod * 12);
            if (res.success) {
                showToast(`Domain renewal processed for ${renewPeriod} year(s)`);
                setModal(m => ({ ...m, open: false }));
            } else {
                showToast(res.message || "Renewal failed", "error");
            }
        } catch (err) {
            showToast("An error occurred", "error");
        } finally {
            setActionLoading(null);
        }
    };

    const handleLogout = () => { localStorage.clear(); navigate('/login'); };
    const formatPrice = (p: any) => { const n = parseFloat(String(p)); return isNaN(n) ? '0.00' : n.toFixed(2); };
    const getInitials = () => {
        const fn = customer?.firstName || customer?.first_name || '';
        const ln = customer?.lastName || customer?.last_name || '';
        if (fn && ln) return `${fn[0]}${ln[0]}`.toUpperCase();
        if (fn) return fn.substring(0, 2).toUpperCase();
        return (session?.email?.[0] || 'U').toUpperCase();
    };
    const getDaysToExpiry = (dateStr: string) => {
        if (!dateStr) return null;
        const diff = new Date(dateStr).getTime() - Date.now();
        return Math.ceil(diff / (1000 * 60 * 60 * 24));
    };
    const getStatusBadge = (dom: any) => {
        const days = getDaysToExpiry(dom.expiryDate);
        if (days !== null && days < 30) return { label: 'Expiring Soon', cls: 'bg-red-500/20 text-red-400 border-red-500/30' };
        if (dom.isLocked) return { label: 'Locked', cls: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
        return { label: 'Active', cls: 'bg-green-500/20 text-green-400 border-green-500/30' };
    };

    const filteredDomains = domains.filter(d => d.domainName?.toLowerCase().includes(domainSearch.toLowerCase()));

    const navItems: { id: TabId; label: string; icon: any; badge?: number }[] = [
        { id: 'overview', label: 'Overview', icon: LayoutDashboard },
        { id: 'domains', label: 'My Domains', icon: Globe, badge: domains.length },
        { id: 'billing', label: 'Billing', icon: CreditCard, badge: invoices.length },
        { id: 'cart', label: 'Cart', icon: ShoppingCart, badge: (cartData?.items.domains?.length || 0) + (cartData?.items.services?.length || 0) || undefined },
        { id: 'account', label: 'Account', icon: User },
    ];

    if (!session) return null;

    return (
        <div className="min-h-screen text-white bg-[#020412] flex flex-col overflow-x-hidden">
            {/* Ambient glows to match home page - ENHANCED for crazy aesthetic */}
            <div className="fixed inset-0 pointer-events-none z-0">
                {/* Main Blue Base Glow */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,_rgba(11,22,64,0.4)_0%,_transparent_50%)]" />

                {/* Dynamic Floating Blobs */}
                <div className="absolute top-[-10%] right-[5%] w-[700px] h-[700px] bg-cyan-500/10 blur-[180px] rounded-full animate-pulse" />
                <div className="absolute bottom-[20%] left-[-10%] w-[600px] h-[600px] bg-blue-600/15 blur-[180px] rounded-full animate-float-slow" />
                <div className="absolute top-[30%] left-[40%] w-[400px] h-[400px] bg-indigo-600/10 blur-[130px] rounded-full" />
                <div className="absolute bottom-[-10%] right-[20%] w-[500px] h-[500px] bg-blue-900/10 blur-[150px] rounded-full" />

                {/* Modern subtle grid like home page */}
                <div className="absolute inset-0 opacity-[0.04]"
                    style={{
                        backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(59,130,246,0.15) 1px, transparent 0)',
                        backgroundSize: '40px 40px'
                    }}
                />

                {/* Gradient overlay for depth */}
                <div className="absolute inset-0 bg-gradient-to-b from-[#020412]/20 via-[#020412]/50 to-[#020412]" />
            </div>

            <Header />

            {/* Toast */}
            {toast && (
                <div className={`fixed top-20 right-6 z-[100] px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-3 text-sm font-medium transition-all animate-in slide-in-from-right-4 ${toast.type === 'success' ? 'bg-green-500/20 border border-green-500/40 text-green-300' : 'bg-red-500/20 border border-red-500/40 text-red-300'}`}>
                    {toast.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    {toast.msg}
                </div>
            )}

            <div className="flex-1 container max-w-7xl mx-auto px-4 pt-8 pb-20 relative z-10">
                {/* Top Profile Strip - Glassmorphism Enhanced */}
                <div className="flex flex-col sm:flex-row items-center gap-5 mb-8 p-6 bg-white/[0.02] backdrop-blur-2xl rounded-3xl border border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.3)] transition-all hover:bg-white/[0.04] hover:border-white/20">
                    <div className="relative group">
                        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600/50 to-indigo-600/50 rounded-full blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
                        <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-3xl font-bold border border-white/20 shadow-2xl flex-shrink-0">
                            <span className="text-white drop-shadow-md">
                                {getInitials()}
                            </span>
                        </div>
                    </div>
                    <div className="flex-1 text-center sm:text-left">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                            <h1 className="text-2xl font-black tracking-tight text-white">
                                {(customer?.firstName || customer?.first_name || customer?.name) ? `${customer.firstName || customer.first_name || customer.name || ''} ${customer.lastName || customer.last_name || ''}`.trim() : (session.username || session.email)}
                            </h1>
                            <span className="hidden sm:inline w-1 h-1 bg-white/20 rounded-full"></span>
                            <div className="flex items-center gap-2 justify-center sm:justify-start">
                                <span className="text-xs font-mono py-1 px-2 bg-white/5 rounded-lg border border-white/10 text-white font-bold tracking-wider uppercase">
                                    Member since {new Date(customer?.createdAt || Date.now()).getFullYear()}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="flex items-center bg-white/5 rounded-2xl p-1 border border-white/10">
                            <button className="p-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition relative">
                                <Bell className="w-5 h-5" />
                                <span className="absolute top-2 right-2 w-2 h-2 bg-cyan-500 rounded-full border-2 border-[#0d0d15]"></span>
                            </button>
                            <button onClick={() => setModal({ open: true, tab: 'settings', domain: { domainName: 'Account Settings' } as any })} className="p-2.5 rounded-xl text-white/60 hover:text-white hover:bg-white/10 transition">
                                <Settings className="w-5 h-5" />
                            </button>
                            <button onClick={handleLogout} className="p-2.5 rounded-xl text-white/60 hover:text-red-400 hover:bg-red-500/10 transition" title="Logout">
                                <LogOut className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Nav */}
                    <aside className="lg:w-64 flex-shrink-0">
                        <nav className="flex lg:flex-col gap-1 p-2 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-x-auto">
                            {navItems.map((item) => (
                                <button
                                    key={item.id}
                                    onClick={() => setActiveTab(item.id)}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300 group ${activeTab === item.id ? 'bg-white/10 text-white shadow-lg shadow-black/20' : 'text-white hover:bg-white/[0.05]'}`}
                                >
                                    <item.icon className={`w-4 h-4 transition-transform group-hover:scale-110 ${activeTab === item.id ? 'text-cyan-400' : ''}`} />
                                    <span className="text-sm font-black tracking-wide flex-1 text-left">{item.label}</span>
                                    {item.badge !== undefined && item.badge > 0 && (
                                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-mono font-bold ${activeTab === item.id ? 'bg-cyan-500 text-black' : 'bg-white/10 text-white/60'}`}>
                                            {item.badge}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </nav>

                        {/* Quick Status Sidebar Card */}
                        <div className="hidden lg:block mt-6 p-6 bg-gradient-to-br from-indigo-600/10 to-blue-600/10 rounded-2xl border border-white/10">
                            <h4 className="text-[10px] uppercase font-black tracking-widest text-white mb-4">Current Status</h4>
                            <div className="space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-white font-bold">Active Domains</span>
                                    <span className="text-sm font-black font-mono text-white">{domains.length}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs text-white font-bold">Unpaid Invoices</span>
                                    <span className="text-sm font-black font-mono text-white">{invoices.filter(i => i.status !== 'paid').length}</span>
                                </div>
                            </div>
                        </div>

                        {/* Session Card - MOVED TO LEFT */}
                        <div className="mt-4 p-5 bg-white/[0.02] backdrop-blur-3xl rounded-3xl border border-white/5 flex items-center justify-between group">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-amber-500/10 transition-colors">
                                    <Clock className="w-5 h-5 text-amber-400/60 group-hover:text-amber-400" />
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase font-black tracking-widest text-white">Session</div>
                                    <div className="text-xs font-black text-white">
                                        {Math.floor(timeRemaining / 60)}h {timeRemaining % 60}m Remaining
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content Area */}
                    <div className="flex-1">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-32 space-y-4 bg-white/[0.01] rounded-3xl border border-white/5">
                                <Loader2 className="w-12 h-12 text-cyan-500 animate-spin" />
                                <p className="text-white/60 font-medium tracking-widest text-xs uppercase">Initializing secure session...</p>
                            </div>
                        ) : error ? (
                            <div className="p-8 bg-red-500/5 rounded-3xl border border-red-500/20 text-center">
                                <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
                                <h3 className="text-lg font-bold text-red-400 mb-1">System connectivity error</h3>
                                <p className="text-white/60 text-sm max-w-md mx-auto">{error}</p>
                                <button onClick={() => window.location.reload()} className="mt-4 px-6 py-2 bg-white/5 hover:bg-white/10 rounded-xl transition text-sm">Retry Connection</button>
                            </div>
                        ) : (
                            <>
                                {/* ===== OVERVIEW ===== */}
                                {activeTab === 'overview' && (
                                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                            {[
                                                { label: 'Domains', val: domains.length, icon: Globe, color: 'text-cyan-400', bg: 'bg-cyan-500/10' },
                                                { label: 'Expirations', val: domains.filter(d => getDaysToExpiry(d.expiryDate) !== null && getDaysToExpiry(d.expiryDate)! < 30).length, icon: Clock, color: 'text-orange-400', bg: 'bg-orange-500/10' },
                                                { label: 'Security', val: domains.filter(d => d.isLocked).length, icon: Lock, color: 'text-blue-400', bg: 'bg-blue-500/10' },
                                                { label: 'Payments', val: orders.length, icon: CreditCard, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
                                            ].map((stat, i) => (
                                                <div key={i} className="p-5 bg-white/[0.02] backdrop-blur-3xl rounded-3xl border border-white/10 hover:bg-white/[0.06] hover:border-white/20 transition-all group overflow-hidden relative">
                                                    <div className={`absolute top-0 right-0 w-24 h-24 ${stat.bg} blur-[40px] rounded-full -mr-12 -mt-12 transition-transform duration-500 group-hover:scale-150`} />
                                                    <div className={`w-10 h-10 ${stat.bg} rounded-xl flex items-center justify-center mb-4 relative z-10`}>
                                                        <stat.icon className={`w-5 h-5 ${stat.color}`} />
                                                    </div>
                                                    <div className="text-3xl font-black mb-1 relative z-10 tracking-tighter text-white">{stat.val}</div>
                                                    <div className="text-[10px] uppercase font-black tracking-widest text-white relative z-10">{stat.label}</div>
                                                </div>
                                            ))}
                                        </div>

                                        <div className="grid lg:grid-cols-3 gap-6">
                                            <div className="lg:col-span-2 bg-white/[0.02] backdrop-blur-3xl rounded-3xl border border-white/10 overflow-hidden">
                                                <div className="p-6 border-b border-white/5 flex items-center justify-between">
                                                    <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-white">Recent Domains</h3>
                                                    <button onClick={() => setActiveTab('domains')} className="text-[10px] font-black uppercase tracking-widest text-white hover:text-white/80 transition-colors flex items-center gap-1 group">
                                                        View all <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                                                    </button>
                                                </div>        <div className="divide-y divide-white/5">
                                                    {domains.slice(0, 5).length > 0 ? domains.slice(0, 5).map((dom, i) => (
                                                        <div key={i} className="p-4 hover:bg-white/[0.02] transition-colors flex items-center justify-between group">
                                                            <div className="flex items-center gap-3">
                                                                <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                                                                    <Globe className="w-5 h-5 text-white/60 group-hover:text-cyan-400 transition-colors" />
                                                                </div>
                                                                <div>
                                                                    <div className="text-sm font-black text-white">{dom.domainName}</div>
                                                                    <div className="text-[10px] text-white font-bold uppercase tracking-tighter">Expires: {dom.expiryDate?.split('T')[0]}</div>
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-4">
                                                                <span className={`text-[9px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-widest ${getStatusBadge(dom).cls}`}>
                                                                    {getStatusBadge(dom).label}
                                                                </span>
                                                                <button onClick={() => { setModal({ open: true, domain: dom, tab: 'info' }); setNameServers(dom.nameServers || []); }} className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition">
                                                                    <Settings className="w-4 h-4" />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )) : (
                                                        <div className="py-20 text-center">
                                                            <div className="w-16 h-16 bg-white/[0.03] rounded-full flex items-center justify-center mx-auto mb-4">
                                                                <Globe className="w-8 h-8 text-white/10" />
                                                            </div>
                                                            <p className="text-white/50 text-sm">No domains yet.</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="space-y-6">
                                                <div className="p-6 bg-gradient-to-br from-[#0c0f24] to-[#060814] rounded-3xl border border-white/10 relative overflow-hidden group">
                                                    <div className="absolute top-0 right-0 w-32 h-32 bg-cyan-500/5 blur-[50px] rounded-full -mr-16 -mt-16 group-hover:bg-cyan-500/10 transition-colors" />
                                                    <h3 className="font-black text-[10px] uppercase tracking-[0.2em] text-white mb-5 flex items-center gap-2">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
                                                        Quick Actions
                                                    </h3>
                                                    <div className="grid grid-cols-2 gap-3">
                                                        {[
                                                            { label: 'Register', icon: Plus, onClick: () => navigate('/domains'), color: 'from-blue-600 to-indigo-700' },
                                                            { label: 'Cart', icon: ShoppingCart, onClick: () => setActiveTab('cart'), color: 'from-purple-600 to-fuchsia-700' },
                                                            { label: 'Invoice', icon: CreditCard, onClick: () => setActiveTab('billing'), color: 'from-emerald-600 to-teal-700' },
                                                            { label: 'Profile', icon: User, onClick: () => setActiveTab('account'), color: 'from-orange-500 to-red-600' },
                                                        ].map((btn, i) => (
                                                            <button
                                                                key={i}
                                                                onClick={btn.onClick}
                                                                className={`flex flex-col items-center justify-center p-4 rounded-2xl bg-gradient-to-br ${btn.color} opacity-80 hover:opacity-100 border border-white/10 hover:scale-[1.02] shadow-[0_0_20px_rgba(0,0,0,0.3)] hover:shadow-cyan-500/10 transition-all group active:scale-[0.98]`}
                                                            >
                                                                <btn.icon className="w-5 h-5 mb-2 text-white group-hover:scale-110 transition-transform" />
                                                                <span className="text-[10px] uppercase font-black tracking-widest text-white">{btn.label}</span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>


                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ===== DOMAINS ===== */}
                                {activeTab === 'domains' && (
                                    <div className="space-y-6 animate-in fade-in duration-300">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                            <h2 className="text-2xl font-black tracking-tight">Your Domains</h2>
                                            <div className="relative">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/60" />
                                                <input
                                                    type="text"
                                                    placeholder="Search domains..."
                                                    value={domainSearch}
                                                    onChange={(e) => setDomainSearch(e.target.value)}
                                                    className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-xl focus:outline-none focus:border-cyan-500/50 transition-colors text-sm"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
                                            {filteredDomains.length > 0 ? filteredDomains.map((dom, i) => (
                                                <div key={i} className="group bg-white/[0.02] backdrop-blur-2xl rounded-3xl border border-white/10 p-6 hover:bg-white/[0.04] transition-all relative overflow-hidden">
                                                    <div className="absolute top-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                                        <Plus className="w-4 h-4 text-white/50" />
                                                    </div>
                                                    <div className="flex justify-between items-start mb-6">
                                                        <div className="w-12 h-12 bg-white/5 rounded-2xl flex items-center justify-center group-hover:bg-cyan-500/20 transition-colors">
                                                            <Globe className="w-6 h-6 text-white/60 group-hover:text-cyan-400 transition-colors" />
                                                        </div>
                                                        <span className={`text-[10px] px-2.5 py-1 rounded-full border font-black uppercase tracking-widest ${getStatusBadge(dom).cls}`}>
                                                            {getStatusBadge(dom).label}
                                                        </span>
                                                    </div>
                                                    <div className="mb-6">
                                                        <h3 className="text-xl font-black tracking-tight text-white mb-1">{dom.domainName}</h3>
                                                        <div className="flex items-center gap-2 text-xs text-white/40">
                                                            <Wifi className="w-3 h-3" />
                                                            <span>Standard Registration</span>
                                                        </div>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-white/5">
                                                        <button onClick={() => { setModal({ open: true, domain: dom, tab: 'ns' }); setNameServers(dom.nameServers || []); }} className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2">
                                                            <Wifi className="w-3.5 h-3.5" />Nameservers
                                                        </button>
                                                        <button onClick={() => { setModal({ open: true, domain: dom, tab: 'info' }); setNameServers(dom.nameServers || []); }} className="px-3 py-2 bg-white/5 hover:bg-white/10 rounded-xl text-xs font-bold transition flex items-center justify-center gap-2">
                                                            <Settings className="w-3.5 h-3.5" />Settings
                                                        </button>
                                                    </div>
                                                </div>
                                            )) : (
                                                <div className="col-span-full py-24 text-center bg-white/[0.01] rounded-3xl border border-dashed border-white/10">
                                                    <Globe className="w-16 h-16 text-white/10 mx-auto mb-4" />
                                                    <p className="text-white/60 font-medium">No domains found matching your search.</p>
                                                    <button onClick={() => navigate('/domains')} className="mt-4 text-cyan-400 text-sm font-bold flex items-center gap-1 mx-auto">Register new domain <Plus className="w-3 h-3" /></button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* ===== BILLING ===== */}
                                {activeTab === 'billing' && (
                                    <div className="space-y-6 animate-in fade-in duration-300">
                                        <h2 className="text-2xl font-black tracking-tight">Billing & Invoices</h2>
                                        <div className="bg-white/[0.02] backdrop-blur-2xl rounded-3xl border border-white/10 overflow-hidden">
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-left">
                                                    <thead>
                                                        <tr className="border-b border-white/10 bg-white/5">
                                                            <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-white/60">Invoice #</th>
                                                            <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-white/60">Date</th>
                                                            <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-white/60">Amount</th>
                                                            <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-white/60">Status</th>
                                                            <th className="px-6 py-4 text-[10px] uppercase font-black tracking-widest text-white/60 text-right">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-white/5">
                                                        {invoices.length > 0 ? invoices.map((inv, i) => (
                                                            <tr key={i} className="hover:bg-white/[0.02] transition-colors">
                                                                <td className="px-6 py-5">
                                                                    <div className="text-sm font-bold font-mono">#{inv.id}</div>
                                                                </td>
                                                                <td className="px-6 py-5">
                                                                    <div className="text-sm text-white/60">{inv.issueDate?.split('T')[0]}</div>
                                                                </td>
                                                                <td className="px-6 py-5">
                                                                    <div className="text-sm font-black text-emerald-400">${formatPrice(inv.amount)}</div>
                                                                </td>
                                                                <td className="px-6 py-5">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase tracking-widest ${inv.status === 'paid' ? 'bg-green-500/10 text-green-400 border-green-500/20' : inv.status === 'test' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' : 'bg-orange-500/10 text-orange-400 border-orange-500/20'}`}>
                                                                            {inv.status || 'Pending'}
                                                                        </span>
                                                                        {inv.isDummy && (
                                                                            <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold tracking-widest">DUMMY</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-5 text-right">
                                                                    <button onClick={() => handleDownloadInvoice(inv.id)} className="text-xs text-white/60 hover:text-white font-bold uppercase tracking-wider transition">Download PDF</button>
                                                                </td>
                                                            </tr>
                                                        )) : (
                                                            <tr>
                                                                <td colSpan={5} className="py-20 text-center text-white/50 text-sm">No billing history available.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ===== CART ===== */}
                                {activeTab === 'cart' && (
                                    <div className="space-y-5 animate-in fade-in duration-300">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-xl font-bold">Shopping Cart</h2>
                                            {((cartData?.items.domains?.length || 0) + (cartData?.items.services?.length || 0)) > 0 && (
                                                <button onClick={() => { clearCartData(); setCartData(null); }} className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1">
                                                    <Trash2 className="w-3 h-3" />Clear Cart
                                                </button>
                                            )}
                                        </div>
                                        {(!cartData?.items.domains?.length && !cartData?.items.services?.length) ? (
                                            <div className="text-center py-20 bg-white/[0.02] rounded-2xl border border-dashed border-white/10">
                                                <ShoppingCart className="w-12 h-12 text-white/10 mx-auto mb-3" />
                                                <p className="text-white/60 text-sm">Your cart is empty.</p>
                                                <GradientButton onClick={() => navigate('/domains')} className="mt-4 text-sm">Browse Domains</GradientButton>
                                            </div>
                                        ) : (
                                            <div className="space-y-3">
                                                {cartData?.items.domains?.map((dom: any, i: number) => (
                                                    <div key={i} className="flex items-center gap-4 p-4 bg-white/[0.03] rounded-xl border border-white/10">
                                                        <Globe className="w-5 h-5 text-cyan-400 flex-shrink-0" />
                                                        <div className="flex-1">
                                                            <div className="font-semibold">{dom.name}</div>
                                                            <div className="text-xs text-white/60">{dom.period || 1} Year(s)</div>
                                                        </div>
                                                        <div className="font-bold">${formatPrice((dom.price || 0) * (dom.period || 1))}</div>
                                                    </div>
                                                ))}
                                                {cartData?.items.services?.map((svc: any, i: number) => (
                                                    <div key={i} className="flex items-center gap-4 p-4 bg-white/[0.02] rounded-xl border border-white/5">
                                                        <Package className="w-5 h-5 text-blue-400 flex-shrink-0" />
                                                        <div className="flex-1">
                                                            <div className="font-semibold">{svc.name}</div>
                                                            <div className="text-xs text-white/60 capitalize">{svc.type?.replace('-', ' ')}</div>
                                                        </div>
                                                        <div className="font-bold">${formatPrice(svc.price)}</div>
                                                    </div>
                                                ))}
                                                <div className="flex items-center justify-between p-5 bg-gradient-to-r from-cyan-600/10 to-blue-600/10 rounded-2xl border border-cyan-500/20 mt-4">
                                                    <div>
                                                        <div className="text-sm text-white/60 mb-1">Total</div>
                                                        <div className="text-3xl font-bold">${formatPrice(calculateTotal(cartData))}</div>
                                                    </div>
                                                    <GradientButton variant="dashboard" onClick={() => navigate('/checkout')} className="px-8 py-3">
                                                        Checkout <ChevronRight className="w-4 h-4 ml-1" />
                                                    </GradientButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* ===== ACCOUNT ===== */}
                                {activeTab === 'account' && (
                                    <div className="space-y-6 animate-in fade-in duration-300">
                                        <div className="flex items-center justify-between">
                                            <h2 className="text-xl font-bold">Account Information</h2>
                                            <button onClick={() => setModal({ open: true, tab: 'edit-profile', domain: { domainName: 'Profile' } as any })} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 bg-cyan-500/5 px-3 py-1.5 rounded-lg border border-cyan-500/20 transition-all">
                                                <Edit3 className="w-3 h-3" />Edit Details
                                            </button>
                                        </div>
                                        <div className="grid md:grid-cols-2 gap-5">
                                            <div className="bg-white/[0.03] rounded-2xl border border-white/10 p-6 space-y-4">
                                                <h3 className="font-medium flex items-center gap-2 text-sm text-white/60 uppercase tracking-wider"><User className="w-4 h-4" />Personal Info</h3>
                                                {(() => {
                                                    const formatVal = (v: any) => {
                                                        if (!v) return 'Not provided';
                                                        if (typeof v === 'object') return v.number || v.phone || v.value || JSON.stringify(v);
                                                        return String(v);
                                                    };
                                                    return [
                                                        ['Full Name', formatVal(`${customer?.firstName || customer?.first_name || customer?.name || ''} ${customer?.lastName || customer?.last_name || ''}`.trim() || 'N/A')],
                                                        ['Email', formatVal(customer?.email || session.email)],
                                                        ['Phone', formatVal(customer?.phone || customer?.phone_number || customer?.telephone || customer?.contact_number)],
                                                        ['Mobile', formatVal(customer?.mobile || customer?.mobile_number || customer?.phone)],
                                                    ].map(([label, val]) => (
                                                        <div key={label} className="flex flex-col gap-0.5">
                                                            <span className="text-xs text-white/60">{label}</span>
                                                            <span className="text-sm font-medium">{val}</span>
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                            <div className="bg-white/[0.03] rounded-2xl border border-white/10 p-6 space-y-4">
                                                <h3 className="font-medium flex items-center gap-2 text-sm text-white/60 uppercase tracking-wider"><Shield className="w-4 h-4" />Address</h3>
                                                {(() => {
                                                    const formatVal = (v: any) => {
                                                        if (!v) return 'Not provided';
                                                        if (typeof v === 'object') return v.street || v.address || v.line1 || v.host || v.number || JSON.stringify(v);
                                                        return String(v);
                                                    };
                                                    return [
                                                        ['Address', formatVal(customer?.address || customer?.street_address || customer?.street || customer?.address1)],
                                                        ['City', formatVal(customer?.city)],
                                                        ['State', formatVal(customer?.state || customer?.province || customer?.state_name)],
                                                        ['Country', formatVal(customer?.country || customer?.country_name || customer?.country_code)],
                                                        ['Post Code', formatVal(customer?.postCode || customer?.post_code || customer?.zip_code || customer?.zip)],
                                                    ].map(([label, val]) => (
                                                        <div key={label} className="flex flex-col gap-0.5">
                                                            <span className="text-xs text-white/60">{label}</span>
                                                            <span className="text-sm font-medium">{val}</span>
                                                        </div>
                                                    ));
                                                })()}
                                            </div>
                                        </div>
                                        <div className="bg-white/[0.03] rounded-2xl border border-white/10 p-6 space-y-3">
                                            <h3 className="font-medium flex items-center gap-2 text-sm text-white/60 uppercase tracking-wider"><ShieldCheck className="w-4 h-4" />Account Security</h3>
                                            <div className="flex items-center justify-between p-4 bg-white/[0.02] rounded-xl border border-white/5">
                                                <div>
                                                    <div className="text-sm font-medium">Verification Status</div>
                                                    {(() => {
                                                        const isBusiness = customer?.accountType === 'business';
                                                        const bNum = customer?.businessNumber || '';
                                                        const bType = customer?.businessNumberType || 'ABN';
                                                        const isValid = isBusiness ? (
                                                            (bType === 'ABN' ? isValidABN(bNum) : bType === 'ACN' ? isValidACN(bNum) : !!bNum)
                                                        ) : true;

                                                        return isValid ? (
                                                            <div className="text-xs text-emerald-400 font-mono mt-0.5 uppercase tracking-widest font-bold">Fully Verified</div>
                                                        ) : (
                                                            <div className="text-xs text-red-400 font-mono mt-0.5 uppercase tracking-widest font-bold text-glow-red">Verification Required: Invalid {bType}</div>
                                                        );
                                                    })()}
                                                </div>
                                                <ShieldCheck className={`w-8 h-8 ${customer?.accountType === 'business' && !(customer?.businessNumberType === 'ABN' ? isValidABN(customer.businessNumber) : customer?.businessNumberType === 'ACN' ? isValidACN(customer.businessNumber) : !!customer.businessNumber) ? 'text-red-500/30' : 'text-emerald-500/30'}`} />
                                            </div>
                                            <div className="flex gap-3 mt-2">
                                                <GradientButton variant="dashboard" onClick={() => navigate('/forgot-password')} className="text-sm py-2 px-4">Change Password</GradientButton>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Domain Management Modal */}
            {
                modal.open && modal.domain && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={() => setModal(m => ({ ...m, open: false }))} />
                        <div className="relative w-full max-w-lg bg-[#0d0d12] border border-white/15 rounded-2xl shadow-2xl overflow-hidden">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-5 border-b border-white/10">
                                <div className="flex items-center gap-3">
                                    <div className="w-9 h-9 bg-cyan-500/10 rounded-lg flex items-center justify-center">
                                        <Globe className="w-4 h-4 text-cyan-400" />
                                    </div>
                                    <div className="max-w-[200px] truncate">
                                        <div className="font-semibold text-sm truncate">{modal.domain.domainName}</div>
                                        <div className="text-[10px] text-white/60 uppercase tracking-widest leading-none">Management</div>
                                    </div>
                                </div>
                                <button onClick={() => setModal(m => ({ ...m, open: false }))} className="p-1.5 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition">
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                            {/* Tab switcher - hide when editing profile */}
                            {modal.tab !== 'edit-profile' && (
                                <div className="flex border-b border-white/10 bg-white/5">
                                    {([['ns', 'Nameservers', Wifi], ['lock', 'Security', Lock], ['renew', 'Renewal', RefreshCw], ['info', 'Details', Eye]] as const).map(([id, label, Icon]) => (
                                        <button key={id} onClick={() => setModal(m => ({ ...m, tab: id }))}
                                            className={`flex-1 px-3 py-3 text-[10px] font-bold uppercase tracking-widest flex flex-col items-center justify-center gap-1.5 transition ${modal.tab === id ? 'text-cyan-400 border-b-2 border-cyan-400 bg-cyan-500/5' : 'text-white/60 hover:text-white/60'}`}>
                                            <Icon className="w-3 h-3" />{label}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <div className="p-5">
                                {/* Nameservers Tab */}
                                {modal.tab === 'ns' && (
                                    <div className="space-y-4">
                                        <p className="text-xs text-white/60">Update nameservers for <strong className="text-white">{modal.domain.domainName}</strong>. Changes may take 24-48 hours to propagate.</p>
                                        <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
                                            {nameServers.map((ns, i) => (
                                                <div key={i} className="flex gap-2">
                                                    <input value={ns.host} onChange={e => { const n = [...nameServers]; n[i] = { host: e.target.value }; setNameServers(n); }}
                                                        placeholder={`ns${i + 1}.example.com`}
                                                        className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-sm focus:outline-none focus:border-cyan-500/50 text-white placeholder-white/20" />
                                                    <button onClick={() => setNameServers(ns => ns.filter((_, j) => j !== i))} disabled={nameServers.length <= 2}
                                                        className="p-2 text-red-400 hover:bg-red-500/10 rounded-xl transition disabled:opacity-30">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button onClick={() => setNameServers(ns => [...ns, { host: '' }])} className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1 ml-1">
                                            <Plus className="w-3 h-3" />Add nameserver
                                        </button>
                                        <GradientButton variant="dashboard" onClick={handleSaveNs} disabled={actionLoading === 'ns'} className="w-full py-3 text-sm font-bold">
                                            {actionLoading === 'ns' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Apply Nameserver Changes'}
                                        </GradientButton>
                                    </div>
                                )}
                                {/* Lock Tab */}
                                {modal.tab === 'lock' && (
                                    <div className="space-y-5">
                                        <div className={`p-4 rounded-2xl border ${modal.domain.isLocked ? 'bg-blue-500/10 border-blue-500/20' : 'bg-white/5 border-white/10'}`}>
                                            <div className="flex items-center gap-3">
                                                {modal.domain.isLocked ? <Lock className="w-5 h-5 text-blue-400" /> : <Unlock className="w-5 h-5 text-white/60" />}
                                                <div>
                                                    <div className="font-bold text-sm">{modal.domain.isLocked ? 'Domain is Protected' : 'Domain is Vulnerable'}</div>
                                                    <div className="text-[10px] text-white/60 mt-0.5 uppercase tracking-widest">{modal.domain.isLocked ? 'Transfer lock is active' : 'Transfer lock is disabled'}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-xs text-white/60 space-y-2 bg-white/5 p-4 rounded-xl">
                                            <p>• Domain locking prevents unauthorized transfers to other registrars.</p>
                                            <p>• We recommend keeping the lock ON unless you are moving your domain.</p>
                                        </div>
                                        <GradientButton variant="dashboard" onClick={() => { handleToggleLock(modal.domain); setModal(m => ({ ...m, open: false })); }} disabled={!!actionLoading} className="w-full py-3 text-sm font-bold">
                                            {modal.domain.isLocked ? <><Unlock className="w-4 h-4 mr-2" />Disable Transfer Lock</> : <><Lock className="w-4 h-4 mr-2" />Enable Transfer Lock</>}
                                        </GradientButton>
                                    </div>
                                )}
                                {/* Renew Tab */}
                                {modal.tab === 'renew' && (
                                    <div className="space-y-5">
                                        <div className="p-4 bg-white/5 rounded-2xl border border-white/10">
                                            <div className="text-sm font-bold mb-1">{modal.domain.domainName}</div>
                                            <div className="text-xs text-white/60">Current Expiry: {modal.domain.expiryDate?.split('T')[0] || 'N/A'}</div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] text-white/60 mb-2 block uppercase font-bold tracking-widest ml-1">Renewal Term</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                {[1, 2, 5].map(y => (
                                                    <button key={y} onClick={() => setRenewPeriod(y)} className={`py-2 rounded-xl text-xs font-bold transition-all ${renewPeriod === y ? 'bg-cyan-500 text-black shadow-lg shadow-cyan-500/20' : 'bg-white/5 text-white/60 hover:bg-white/10 hover:text-white'}`}>
                                                        {y} Year{y > 1 ? 's' : ''}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <GradientButton variant="dashboard" onClick={handleRenew} disabled={actionLoading === 'renew'} className="w-full py-3 text-sm font-bold">
                                            {actionLoading === 'renew' ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : <><RefreshCw className="w-4 h-4 mr-2" />Renew Registration</>}
                                        </GradientButton>
                                    </div>
                                )}
                                {/* Details Tab */}
                                {modal.tab === 'info' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                                                <div className="text-[10px] text-white/60 uppercase font-black tracking-widest">Registrant</div>
                                                <div className="text-xs font-bold mt-1 truncate">{customer?.firstName || customer?.first_name} {customer?.lastName || customer?.last_name}</div>
                                            </div>
                                            <div className="bg-white/5 p-3 rounded-xl border border-white/5">
                                                <div className="text-[10px] text-white/60 uppercase font-black tracking-widest">DS ID</div>
                                                <div className="text-xs font-bold mt-1 font-mono">#{modal.domain.id}</div>
                                            </div>
                                        </div>
                                        <div className="bg-white/5 p-4 rounded-xl border border-white/5 space-y-3">
                                            <h4 className="text-[10px] uppercase font-black text-white/50 tracking-[.2em]">Live Registry Status</h4>
                                            <div className="space-y-2">
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-white/60">Registration Status</span>
                                                    <span className="text-emerald-400 font-bold uppercase tracking-tighter">{modal.domain.status || 'Active'}</span>
                                                </div>
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-white/60">Transfer Protection</span>
                                                    <span className={modal.domain.isLocked ? 'text-blue-400 font-bold' : 'text-orange-400'}>{modal.domain.isLocked ? 'Active' : 'Disabled'}</span>
                                                </div>
                                                <div className="flex justify-between text-xs">
                                                    <span className="text-white/60">DS Panel Sync</span>
                                                    <span className="text-cyan-400 font-bold">Synchronized</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Edit Profile Tab */}
                                {modal.tab === 'edit-profile' && (
                                    <form onSubmit={handleUpdateProfile} className="space-y-4">
                                        <p className="text-xs text-white/60 mb-2 italic">Update your primary registration details used for domain ownership.</p>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">First Name</label>
                                                <input name="firstName" defaultValue={customer?.firstName || customer?.first_name} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" required />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Last Name</label>
                                                <input name="lastName" defaultValue={customer?.lastName || customer?.last_name} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" required />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Phone</label>
                                                <div className="relative">
                                                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-xs font-bold pointer-events-none">
                                                        +{findCountryDialCode(selectedCountry)}
                                                    </div>
                                                    <input
                                                        name="phone"
                                                        defaultValue={customer?.phone || customer?.phone_number || customer?.telephone}
                                                        className="w-full pl-12 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Business Name (Required for Business)</label>
                                                <input name="businessName" defaultValue={customer?.businessName || customer?.business_name} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">ID Type</label>
                                                <select name="businessNumberType" defaultValue={customer?.businessNumberType || customer?.business_number_type || 'ABN'} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none appearance-none">
                                                    <option value="ABN" className="bg-[#1a1a2e]">ABN (Australia)</option>
                                                    <option value="ACN" className="bg-[#1a1a2e]">ACN (Australia)</option>
                                                    <option value="Company Number" className="bg-[#1a1a2e]">Company Number</option>
                                                    <option value="Tax ID" className="bg-[#1a1a2e]">Tax ID / EIN</option>
                                                    <option value="Other" className="bg-[#1a1a2e]">Other</option>
                                                </select>
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Business Number</label>
                                                <input name="businessNumber" defaultValue={customer?.businessNumber || customer?.business_number} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" placeholder="12 345 678 901" />
                                            </div>
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Street Address</label>
                                            <input name="address" defaultValue={customer?.address || customer?.street_address || customer?.street} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" />
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">City</label>
                                                <input name="city" defaultValue={customer?.city} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">State / Province</label>
                                                <input name="state" defaultValue={customer?.state || customer?.province} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" />
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Post Code</label>
                                                <input name="postCode" defaultValue={customer?.postCode || customer?.post_code || customer?.zip} className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none" />
                                            </div>
                                            <div className="space-y-1">
                                                <label className="text-[10px] uppercase text-white/60 font-bold ml-1">Country</label>
                                                <select
                                                    name="country"
                                                    value={selectedCountry}
                                                    onChange={(e) => setSelectedCountry(e.target.value)}
                                                    className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm focus:border-cyan-500/50 outline-none appearance-none"
                                                >
                                                    {countries.map(c => (
                                                        <option key={c.code} value={c.code} className="bg-[#1a1a2e] text-white">
                                                            {c.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="flex gap-4 pt-2">
                                            <button type="button" onClick={() => setModal(m => ({ ...m, open: false }))} className="flex-1 py-3 text-sm font-bold text-white/60 hover:text-white transition uppercase tracking-widest">Cancel</button>
                                            <GradientButton type="submit" variant="dashboard" disabled={actionLoading === 'update'} className="flex-[2] py-3 text-sm font-bold flex items-center justify-center gap-2">
                                                {actionLoading === 'update' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                                                Save Profile Details
                                            </GradientButton>
                                        </div>
                                    </form>
                                )}

                                {/* Settings Tab */}
                                {modal.tab === 'settings' && (
                                    <form onSubmit={handleUpdateSettings} className="space-y-5">
                                        <div>
                                            <h4 className="text-[10px] uppercase font-black text-cyan-400 tracking-widest mb-3 flex items-center gap-2">
                                                <Bell className="w-3 h-3" /> Notifications
                                            </h4>
                                            <div className="space-y-2">
                                                {[
                                                    { id: 'n1', label: 'Domain Expiry Alerts', desc: 'Email reminders before domains expire', icon: Clock },
                                                    { id: 'n2', label: 'Security Notifications', desc: 'Alerts for login and security changes', icon: Shield },
                                                    { id: 'n3', label: 'Marketing & Offers', desc: 'Promotions and newsletter updates', icon: Mail },
                                                ].map(n => (
                                                    <label key={n.id} className="flex items-center justify-between p-3 bg-white/[0.03] border border-white/10 rounded-xl cursor-pointer hover:bg-white/[0.05] transition">
                                                        <div className="flex items-center gap-3">
                                                            <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center text-white/60"><n.icon className="w-4 h-4" /></div>
                                                            <div>
                                                                <div className="text-sm font-medium">{n.label}</div>
                                                                <div className="text-[10px] text-white/60 font-medium">{n.desc}</div>
                                                            </div>
                                                        </div>
                                                        <input type="checkbox" defaultChecked={n.id !== 'n3'} className="w-4 h-4 rounded bg-cyan-500/20 border-white/20 text-cyan-500 focus:ring-cyan-500/50" />
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        <div>
                                            <h4 className="text-[10px] uppercase font-black text-blue-400 tracking-widest mb-3 flex items-center gap-2">
                                                <CardIcon className="w-3 h-3" /> Payment Methods
                                            </h4>
                                            <div className="space-y-2">
                                                <div className="p-4 bg-gradient-to-br from-indigo-500/20 to-blue-500/20 border border-white/10 rounded-2xl flex items-center justify-between group">
                                                    <div className="flex items-center gap-4">
                                                        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center border border-white/20"><CardIcon className="w-5 h-5 text-indigo-400" /></div>
                                                        <div>
                                                            <div className="text-sm font-bold flex items-center gap-2">•••• 4242 <span className="text-[10px] px-1.5 bg-indigo-500 text-white rounded uppercase">Primary</span></div>
                                                            <div className="text-[10px] text-white/60 font-mono">EXPIRES 12/26</div>
                                                        </div>
                                                    </div>
                                                    <button type="button" className="text-[10px] font-bold text-white/50 group-hover:text-red-400 transition">Remove</button>
                                                </div>
                                                <button type="button" className="w-full py-3 border border-dashed border-white/20 rounded-xl text-xs font-bold text-white/60 hover:border-cyan-500/50 hover:text-cyan-400 transition flex items-center justify-center gap-2 uppercase tracking-widest">
                                                    <Plus className="w-3 h-3" /> Add New Card
                                                </button>
                                            </div>
                                        </div>

                                        <div className="flex gap-4 pt-4 border-t border-white/10">
                                            <button type="button" onClick={() => setModal(m => ({ ...m, open: false }))} className="flex-1 py-3 text-sm font-bold text-white/60 hover:text-white transition">Close</button>
                                            <GradientButton type="submit" variant="dashboard" className="flex-[2] py-3 text-sm font-bold">
                                                Save Preferences
                                            </GradientButton>
                                        </div>
                                    </form>
                                )}
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
}
